// React is used implicitly for JSX
import { X, Settings, LogOut, Edit, Bookmark, Sun, Moon, MessageSquare, BarChart, Users } from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useState, useEffect, useRef } from 'react';
import SwitchAccountsDialog from './dialogs/SwitchAccountsDialog';
import SwitchAccountsBottomSheet from '../pwa/SwitchAccountsBottomSheet';

interface UserMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Displays a user menu with profile information, navigation links, theme toggle, and sign-out functionality.
 *
 * Renders as a modal overlay when {@link isOpen} is true, allowing users to access profile actions, navigate to related pages, switch between light and dark themes, and log out.
 *
 * @param isOpen - Whether the menu is visible.
 * @param onClose - Callback invoked to close the menu.
 */
export default function UserMenu({ isOpen, onClose }: UserMenuProps) {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isSwitchAccountsOpen, setIsSwitchAccountsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleSignOut = async () => {
    console.log('Logging out...');

    // Close the menu immediately for better UX
    onClose();

    try {
      // Handle logout asynchronously
      await signOut();
      console.log('Logout successful');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const navigateAndClose = (path: string) => {
    console.log('Navigating to:', path);
    onClose();
    navigate(path);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add event listener when the menu is open
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener when the component unmounts or the menu closes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] md:fixed md:inset-auto md:left-0 md:top-auto md:bottom-0 md:w-64"
    >
      <div className="absolute inset-0 bg-black bg-opacity-50 md:hidden" />
      <div
        ref={menuRef}
        className="modal-content absolute bottom-0 left-0 right-0 md:absolute md:left-0 md:bottom-0 md:w-64 bg-white dark:bg-gray-900 rounded-t-xl md:rounded-none md:h-auto md:border-t md:border-gray-200 md:dark:border-gray-800 md:shadow-none overflow-y-scroll scrollbar-hide max-h-[80vh] md:max-h-[calc(100vh-4rem)]"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold dark:text-white">Menu</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4">
          <button
            onClick={() => navigateAndClose(`/profile/${user?.user_metadata?.username || user?.id}`)}
            className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white z-[110] relative cursor-pointer"
          >
            <img
              src={user?.user_metadata?.avatar_url || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde"}
              alt="User avatar"
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="text-left">
              <h3 className="font-semibold dark:text-white">{user?.user_metadata?.full_name || 'User'}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">@{user?.user_metadata?.username || 'username'}</p>
            </div>
          </button>

          <div className="mt-6 space-y-2">
            {/* Switch Accounts Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsSwitchAccountsOpen(true);
                // Close the UserMenu when opening SwitchAccountsBottomSheet
                onClose();
                
                // Small delay to ensure UserMenu closes completely before opening SwitchAccountsBottomSheet
                setTimeout(() => {
                  setIsSwitchAccountsOpen(true);
                }, 100);
              }}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 relative cursor-pointer"
            >
              <Users className="w-5 h-5" />
              <span>Switch Accounts</span>
            </button>

            <button
              onClick={() => navigateAndClose('/message')}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 z-[110] relative cursor-pointer"
            >
              <MessageSquare className="w-5 h-5" />
              <span>Messages</span>
            </button>
            <button
              onClick={() => navigateAndClose('/profile/edit')}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 z-[110] relative cursor-pointer"
            >
              <Edit className="w-5 h-5" />
              <span>Edit Profile</span>
            </button>
            <button
              onClick={() => navigateAndClose('/bookmarks')}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 z-[110] relative cursor-pointer"
            >
              <Bookmark className="w-5 h-5" />
              <span>Bookmarks</span>
            </button>
            <button
              onClick={() => navigateAndClose('/analytics')}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 z-[110] relative cursor-pointer"
            >
              <BarChart className="w-5 h-5" />
              <span>Analytics</span>
            </button>
            {/* Admin panel option removed */}
            <button
              onClick={() => navigateAndClose('/settings')}
              className="flex items-center space-x-3 px-4 py-3 hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white w-full text-left"
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </button>
            <button
              onClick={toggleTheme}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-gray-700 dark:text-gray-300 z-[110] relative cursor-pointer"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              <span>{theme === 'dark' ? "Light Mode" : "Dark Mode"}</span>
            </button>
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-3 w-full p-3 rounded-lg hover:bg-[#065900] hover:text-white dark:hover:bg-[#065900] dark:hover:text-white text-red-500 z-[110] relative cursor-pointer"
            >
              <LogOut className="w-5 h-5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Switch Accounts Dialog - Desktop */}
      <div className="hidden md:block">
        <SwitchAccountsDialog
          isOpen={isSwitchAccountsOpen}
          onClose={() => setIsSwitchAccountsOpen(false)}
        />
      </div>

      {/* Switch Accounts Bottom Sheet - Mobile */}
      <div className="block md:hidden">
        <SwitchAccountsBottomSheet
          isOpen={isSwitchAccountsOpen}
          onClose={() => setIsSwitchAccountsOpen(false)}
        />
      </div>
    </div>
  );
}
